/**
 * Reports crashes and unhandled errors to the Worker's /api/client-errors. Each React root installs it once and passes
 * what it returns to createRoot, so every error a boundary catches is reported once, from the root;
 * boundaries themselves don't report. Window `error` and `unhandledrejection` events are caught too.
 *
 * Installed by the app (index.tsx). It imports nothing from the app, and calls fetch directly rather than fetchApi on
 * purpose: it has to work when the session or fetchApi is what broke, and must never sign anyone
 * out or report its own failures.
 */
import type { RootOptions } from 'react-dom/client';

export type ClientErrorSource = 'app';
export type ClientErrorKind = 'render' | 'uncaught' | 'recoverable' | 'chunk' | 'error' | 'unhandledrejection';

export interface ClientErrorReporting {
  source: ClientErrorSource;
  /** Where reports go, or a function that finds it (null: nowhere yet, so nothing is sent). */
  endpoint: string | (() => string | null | Promise<string | null>);
  /** Extra headers per report: the app sends its token so the log says who it was. */
  headers?: () => Record<string, string>;
  /** The running build, so a stale tab can be told from a new bug. */
  buildId?: string;
}

/** Enough for one person's bad afternoon; a loop of errors stops here rather than flooding the log. */
const MAX_REPORTS_PER_PAGE = 10;
const MAX_STACK = 4000;

let config: ClientErrorReporting | null = null;
let windowListenersAdded = false;
const reported = new Set<string>();
let sentCount = 0;

/** A page's code file that no longer exists (a deploy since the tab opened) or didn't arrive. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') return true;
  return (
    typeof message === 'string' &&
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(
      message
    )
  );
}

function describe(error: unknown): { name?: string; message: string; stack?: string } {
  if (error instanceof Error) return { name: error.name, message: error.message || error.name, stack: error.stack };
  if (typeof error === 'string') return { message: error };
  // DOMException and other error-like objects that aren't Errors everywhere.
  const like = error as { name?: unknown; message?: unknown; stack?: unknown } | null;
  if (like && typeof like.message === 'string') {
    return {
      name: typeof like.name === 'string' ? like.name : undefined,
      message: like.message || String(like.name ?? 'Error'),
      stack: typeof like.stack === 'string' ? like.stack : undefined,
    };
  }
  try {
    return { message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { message: String(error) };
  }
}

/** Browser noise that isn't a factory bug. */
function ignored(name: string | undefined, message: string, stack: string | undefined): boolean {
  if (name === 'AbortError') return true;
  if (/^ResizeObserver loop/.test(message)) return true;
  // A cross-origin script's error: the browser hides everything about it.
  return message === 'Script error.' && !stack;
}

async function send(payload: Record<string, unknown>): Promise<void> {
  const current = config;
  if (!current) return;
  try {
    const url = typeof current.endpoint === 'function' ? await current.endpoint() : current.endpoint;
    if (!url) return;
    await fetch(url, {
      method: 'POST',
      // Still sent if the page is closing or reloading after the crash.
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...current.headers?.() },
      body: JSON.stringify(payload),
    });
  } catch {
    // Reporting must never cause an error of its own.
  }
}

/** Report one error. Repeats of the same error on a page, and anything past the page's cap, are dropped. */
export function reportClientError(error: unknown, kind: ClientErrorKind, componentStack?: string | null): void {
  if (!config) return;
  const { name, message, stack } = describe(error);
  if (ignored(name, message, stack)) return;
  const finalKind: ClientErrorKind = isChunkLoadError(error) ? 'chunk' : kind;
  const key = `${finalKind}|${message}|${stack?.split('\n')[1] ?? ''}`;
  if (reported.has(key) || sentCount >= MAX_REPORTS_PER_PAGE) return;
  reported.add(key);
  sentCount += 1;
  void send({
    source: config.source,
    kind: finalKind,
    message: message.slice(0, 2000),
    name: name?.slice(0, 200),
    stack: stack?.slice(0, MAX_STACK),
    component_stack: componentStack?.slice(0, MAX_STACK) || undefined,
    // Never the query string or hash: they can carry tokens.
    path: typeof location !== 'undefined' ? location.pathname : undefined,
    build_id: config.buildId,
  });
}

function onWindowError(event: ErrorEvent) {
  reportClientError(event.error ?? event.message, 'error');
}

function onUnhandledRejection(event: PromiseRejectionEvent) {
  reportClientError(event.reason, 'unhandledrejection');
}

/**
 * Start reporting for this page, and get the createRoot options that report what React catches:
 * `createRoot(container, installClientErrorReporting({ source: 'app', endpoint: '/api/client-errors' }))`.
 */
export function installClientErrorReporting(
  options: ClientErrorReporting
): Pick<RootOptions, 'onCaughtError' | 'onUncaughtError' | 'onRecoverableError'> {
  config = options;
  if (!windowListenersAdded && typeof window !== 'undefined') {
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    windowListenersAdded = true;
  }
  // These replace React's own console logging, so they log too.
  return {
    onCaughtError(error: unknown, info: { componentStack?: string | null }) {
      console.error('[react] an error boundary caught', error, info.componentStack);
      reportClientError(error, 'render', info.componentStack);
    },
    onUncaughtError(error: unknown, info: { componentStack?: string | null }) {
      console.error('[react] uncaught error', error, info.componentStack);
      reportClientError(error, 'uncaught', info.componentStack);
    },
    onRecoverableError(error: unknown, info: { componentStack?: string | null }) {
      console.warn('[react] recovered from', error);
      reportClientError(error, 'recoverable', info.componentStack);
    },
  };
}

/** Tests only: forget the configuration and what was sent. */
export function resetClientErrorReportingForTests(): void {
  config = null;
  reported.clear();
  sentCount = 0;
}
